import React, { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { useIntercom } from 'react-use-intercom';
import * as Sentry from '@sentry/react';
import { Dropdown, Button, Tooltip } from '../ui';
import { DropdownWrapper, DropdownListItem } from '../ui/Dropdown/Dropdown';
import TopbarUserChangePasswordModal from '../TopbarUserChangePasswordModal/TopbarUserChangePasswordModal';
import { USER_ROLES_DICTIONARY } from '../../consts/dictionaries';
import { windowMaxWidth } from '../ui/Layout/breakpoints';
import { IconAdministration, IconHelp, IconLogout, IconPassword } from '../ui/Icons';
import checkUiRights from '../../helpers/checkUiRights';
import { findMetaDataDescriptionByProperty, isMobile } from '../../helpers/utility';
import { logout } from '../../redux/authentication/slice';
import { selectCurrentUser, selectUiRights } from '../../redux/authentication/selectors';
import { changePassword, resetChangePasswordStatus } from '../../redux/password/slice';
import { selectChangePasswordLoading, selectChangePasswordFailed } from '../../redux/password/selectors';

const HELP_CENTER_URL = 'https://help.oqio.de/de?utm_source=navigation&utm_medium=inproduct';
const HELP_CENTER_LABEL = 'Hilfe Center';

const UserDetailsWrapper = styled.div`
  border-bottom: 1px solid var(--greyscale-dark-white);
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 16px;
`;

const UserPasswordWrapper = styled.div`
  button {
    display: flex;
    justify-content: flex-start;

    svg {
      path:last-child {
        fill: var(--primary-oqio-blue);
      }
    }
  }
`;

const UserLogoutWrapper = styled.div`
  button {
    display: flex;
    justify-content: flex-start;
  }
`;

const UserCompany = styled.div`
  color: var(--greyscale-blackish);
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-size: var(--font-size-small);
  font-variant-numeric: lining-nums tabular-nums;
  line-height: var(--font-line-height-small);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserName = styled.div`
  font-size: var(--font-size-heading-3);
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserNameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  overflow: hidden;
`;

const UserRolePill = styled.div`
  align-items: flex-start;
  align-self: flex-start;
  background-color: var(--greyscale-dark-white);
  border-radius: 10px;
  color: var(--greyscale-blackish);
  display: flex;
  flex-shrink: 0;
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-size: var(--font-size-small);
  font-variant-numeric: lining-nums tabular-nums;
  font-weight: var(--font-weight-medium);
  line-height: var(--font-line-height-small);
  padding: 2px 10px;
  white-space: nowrap;
`;

const Avatar = styled.div`
  background-color: ${({ $isOpen }) => ($isOpen ? 'var(--greyscale-blackish)' : 'var(--greyscale-dark-white)')};
  border-radius: var(--border-radius-circle);
  color: ${({ $isOpen }) => ($isOpen ? 'var(--greyscale-white)' : 'var(--greyscale-blackish)')};
  cursor: pointer;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  height: 30px;
  line-height: var(--font-line-height-large);
  text-align: center;
  width: 30px;
`;

const AdministrationNavButton = styled.div`
  align-items: center;
  background-color: ${({ $isActive }) => ($isActive ? 'var(--greyscale-blackish)' : 'var(--greyscale-dark-white)')};
  border-radius: var(--border-radius-circle);
  cursor: pointer;
  display: flex;
  height: 30px;
  justify-content: center;
  line-height: 0;
  width: 30px;

  svg path,
  svg circle {
    stroke: ${({ $isActive }) => ($isActive ? 'var(--greyscale-white)' : '#111')};
  }
`;

const HelpCenterLink = styled.a`
  display: flex;
  line-height: 0;

  &:hover {
    circle,
    path {
      stroke: var(--greyscale-blackish);
    }

    path:last-child {
      fill: var(--greyscale-blackish);
    }
  }
`;

const TopbarUserMenuButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: flex-end;
  width: 25%;

  @media ${windowMaxWidth.splitScreen} {
    width: 70px;
  }
`;

const TopbarUserMenuActionsWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`;

const AdministrationNavButtonWrapper = styled.div`
  @media ${windowMaxWidth.splitScreenNarrow} {
    display: none;
  }
`;

const UserMenuDropdownWrapper = styled(DropdownWrapper)`
  max-width: 350px;
  min-width: 300px;
`;

/**
 * Functional component that renders the currentUser menu in the top bar.
 * Includes the currentUser avatar, dropdown menu with options to change password and logout,
 * and handles the display of the change password modal.
 * @property {Object} currentUser - the current logged-in currentUser information.
 * @property {Object} uiRights - the current logged-in currentUser rights and permissions.
 * @property {Boolean}  - a boolean indicating if there was an error during password change.
 * @property {Boolean} changePasswordLoading - a boolean indicating if the password change process is loading.
 * @property {Function} resetChangePasswordStatus - a function that clears currentUser-related errors.
 * @property {Function} changePassword - a function that triggers the password change process.
 * @property {Function} logout - a function that logs out the currentUser.
 */
const TopbarUserMenu = ({
  changePasswordFailed,
  resetChangePasswordStatus,
  changePassword,
  changePasswordLoading,
  logout,
  currentUser,
  uiRights
}) => {
  if (!uiRights || typeof uiRights !== 'object') {
    return;
  }

  const { first_name, last_name, role } = currentUser;

  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [openTopbarUserMenu, setOpenTopbarUserMenu] = useState(false);

  const location = useLocation();
  const isAdministrationActive = location.pathname.startsWith('/administration');

  const { shutdown } = useIntercom();
  const { edit_password_ui } = uiRights;

  // Toggle the change password modal visibility
  const handleToggleChangePasswordModal = () => {
    handleOpenChange(false);
    setShowChangePasswordModal(!changePasswordFailed ? !showChangePasswordModal : false);
  };

  // Close the change password modal with error
  const handleCloseChangePasswordModalWithError = () => {
    resetChangePasswordStatus();
    handleToggleChangePasswordModal();
  };

  // Handle currentUser logout
  const handleUserLogOut = () => {
    shutdown();
    logout();
    Sentry.setUser(null);
  };

  // Open/close the topbar currentUser menu
  const handleOpenChange = (value) => setOpenTopbarUserMenu(value);

  const organizationName = findMetaDataDescriptionByProperty(currentUser, 'organization_name', '', 'description');
  const isChangePasswordModalVisible = showChangePasswordModal || changePasswordLoading || changePasswordFailed;
  const fullName = `${first_name} ${last_name}`;
  const userRole = USER_ROLES_DICTIONARY[role] || role;
  const firstName = first_name.slice(0, 1).toUpperCase();
  const lastName = last_name.slice(0, 1).toUpperCase();

  // Render the currentUser details section
  const renderUserDetails = () => (
    <UserDetailsWrapper>
      <UserNameBlock>
        <UserCompany>{organizationName}</UserCompany>
        <UserName>{fullName}</UserName>
      </UserNameBlock>
      <UserRolePill>{userRole}</UserRolePill>
    </UserDetailsWrapper>
  );

  // Render the change password button based on the users rights
  const renderSetPasswordButton = () => {
    if (!edit_password_ui) return;

    return (
      <>
        <DropdownListItem>
          <UserPasswordWrapper>
            <Button type="text" onClick={handleToggleChangePasswordModal} icon={<IconPassword />} block>
              Passwort ändern
            </Button>
          </UserPasswordWrapper>
        </DropdownListItem>
      </>
    );
  };

  // Render the logout button
  const renderLogoutButton = () => (
    <DropdownListItem>
      <UserLogoutWrapper>
        <Button type="text" onClick={handleUserLogOut} icon={<IconLogout />} block>
          Abmelden
        </Button>
      </UserLogoutWrapper>
    </DropdownListItem>
  );

  // Render the dropdown menu content
  const popupRender = () => (
    <UserMenuDropdownWrapper>
      {renderUserDetails()}
      {renderSetPasswordButton()}
      {renderLogoutButton()}
    </UserMenuDropdownWrapper>
  );

  const helpCenterLink = (
    <HelpCenterLink href={HELP_CENTER_URL} target="_blank" rel="noopener noreferrer" aria-label={HELP_CENTER_LABEL}>
      <IconHelp />
    </HelpCenterLink>
  );

  return (
    <TopbarUserMenuButtonWrapper>
      {isMobile ? (
        helpCenterLink
      ) : (
        <Tooltip mouseEnterDelay={0.15} title={HELP_CENTER_LABEL} placement="left">
          {helpCenterLink}
        </Tooltip>
      )}
      <TopbarUserMenuActionsWrapper>
        {checkUiRights(uiRights, ['administration_ui']) && (
          <AdministrationNavButtonWrapper>
            <Link to="/administration">
              <AdministrationNavButton $isActive={isAdministrationActive}>
                <IconAdministration />
              </AdministrationNavButton>
            </Link>
          </AdministrationNavButtonWrapper>
        )}
        <Dropdown onOpenChange={handleOpenChange} open={openTopbarUserMenu} popupRender={popupRender}>
          <Avatar
            $isOpen={openTopbarUserMenu}
            data-intercom-target="main-navigation-currentUser-menu"
          >{`${firstName}${lastName}`}</Avatar>
        </Dropdown>
      </TopbarUserMenuActionsWrapper>
      {isChangePasswordModalVisible && (
        <TopbarUserChangePasswordModal
          changePassword={changePassword}
          onCancel={changePasswordFailed ? handleCloseChangePasswordModalWithError : handleToggleChangePasswordModal}
          userId={currentUser?.id}
          visible
          changePasswordFailed={changePasswordFailed}
        />
      )}
    </TopbarUserMenuButtonWrapper>
  );
};

const mapStateToProps = (state) => ({
  changePasswordLoading: selectChangePasswordLoading(state),
  changePasswordFailed: selectChangePasswordFailed(state),
  currentUser: selectCurrentUser(state),
  uiRights: selectUiRights(state)
});

const mapDispatchToProps = {
  logout,
  resetChangePasswordStatus,
  changePassword
};

export default connect(mapStateToProps, mapDispatchToProps)(TopbarUserMenu);
